/**
 * Select — combobox customizado portado da ui-foundation do crm_react
 * (SPEC-20260503-1637), adaptado para a paleta slate do orchestrator.
 *
 * API espelha o original: `value` controlado + `options: { value, label }[]`.
 * Trigger é um button que abre/fecha dropdown absoluto. Click-outside fecha.
 * Item ativo destaca com `bg-primary` + `text-primary-foreground`.
 *
 * Substitui `<select>` HTML nativo (proibido por convenção; ver SPEC-1637 +
 * memory feedback `wynk_crm/crm_react: ui-foundation é FONTE DA VERDADE`).
 */

import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "../../utils/cn";

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps {
  value: string | number;
  options: SelectOption[];
  onChange: (value: string | number) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** ARIA: id de elemento label que descreve este Select. */
  "aria-labelledby"?: string;
}

export function Select({
  value,
  options,
  onChange,
  placeholder = "Selecione…",
  disabled = false,
  className,
  id,
  "aria-labelledby": ariaLabelledBy,
}: SelectProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => String(o.value) === String(value));

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={ref} className={cn("relative w-full", className)}>
      <button
        type="button"
        id={id}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby={ariaLabelledBy}
        className={cn(
          "h-9 w-full rounded-md border bg-surface px-3 text-sm text-foreground",
          "flex items-center justify-between cursor-pointer text-left",
          "transition-shadow",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:ring-offset-background",
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-surface-2",
          open ? "border-primary ring-2 ring-primary ring-offset-1 ring-offset-background" : "border-border-strong",
        )}
        onClick={() => !disabled && setOpen((p) => !p)}
      >
        <span className={selected ? "" : "text-muted-foreground/60"}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown
          size={14}
          className={cn(
            "ml-2 shrink-0 text-muted-foreground transition-transform duration-150",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>

      {open && !disabled && (
        <ul
          role="listbox"
          className={cn(
            "absolute top-[calc(100%+4px)] left-0 right-0 z-50",
            "bg-surface border border-border-strong rounded-md shadow-lg",
            "max-h-60 overflow-y-auto py-1 m-0 list-none",
          )}
        >
          {options.map((o) => {
            const isActive = String(o.value) === String(value);
            return (
              <li
                key={o.value}
                role="option"
                aria-selected={isActive}
                className={cn(
                  "px-3 py-2 text-sm cursor-pointer transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground hover:bg-primary-hover"
                    : "text-foreground hover:bg-primary-soft hover:text-primary-soft-fg",
                )}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default Select;
